/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **An authored `bidfloor` the source's terms will raise, said where it is authored** (`#918`, ruled
 * by Todd 2026-09-18: *"it should have the same rules, but it would be a good validation warning"*).
 *
 * The driver bounds every impression's floor **after** everything that can write one
 * (`OpenRtbDriver.applyBidFloorBounds`): a contractual minimum raises it, a fixed CPM sets it. That
 * order is deliberate and unchanged. What was missing is the sentence: an operator set
 * `imp[last].bidfloor: 5` on a child whose floor of 6 came from its parent, saw 6 on every
 * impression, and read it as the override being ignored.
 *
 * The 2026-08-19 floor ruling — *do not report a contractual raise* — is about the **resolution**
 * after the fact. This is an authoring-time warning about a value that will not go out as written,
 * which is what every other value warning is. One rule and one sentence for every surface that
 * authors a floor, so the ORTB body and a `bidfloor` parameter row cannot come to say it differently.
 */

import type { ArtifactInheritance } from '../types';
import { pathInheritance } from './artifact-inheritance';

/** What the source's terms do to any floor authored beneath them. */
export interface FloorBound {
  /** The source's price (`terms.price`) — its own, or its parent's. */
  value: number;
  /** A **fixed** price: the value is the agreed price and **replaces** any authored floor. */
  fixed: boolean;
  /** The parent the value is inherited from, by name; `null` where it is the source's own. */
  inheritedFrom: string | null;
}

/**
 * The bound a source's **price** puts on authored floors, or `null` where it states none (`terms.price`
 * since slice `3c-i`, which replaced `floorCpm` + `cpmIsFixed`). Takes the stored price or the form's.
 *
 * It reads what the source **states**, with its parent's through `P3a`. A price stated by the source's
 * partner or by *Standard Terms* is in force too and is not seen here — that is `3c-ii`'s Terms box,
 * which shows the value in force; until then no surface writes a partner, so nothing states one.
 */
export function floorBoundOf(
  price: { type?: string | null; value?: number | string | null } | null | undefined,
  inheritance: ArtifactInheritance | null,
  parentName: string | null,
): FloorBound | null {
  const raw = price?.value;
  const value = typeof raw === 'number' ? raw : raw != null && String(raw).trim() !== '' ? Number(raw) : NaN;
  if (!Number.isFinite(value) || value < 0) return null;
  const inherited = pathInheritance(inheritance, 'terms.price').state === 'inherited';
  return { value, fixed: price?.type === 'fixed', inheritedFrom: inherited ? (parentName ?? 'its parent') : null };
}

/** A literal floor as authored — a number, a numeric string, or a `$static` node holding one. */
export function authoredFloorLiteral(value: unknown): number | null {
  const inner =
    value && typeof value === 'object' && '$static' in value ? (value as { $static: unknown }).$static : value;
  if (typeof inner === 'number') return Number.isFinite(inner) ? inner : null;
  if (typeof inner === 'string' && inner.trim() !== '' && Number.isFinite(Number(inner))) return Number(inner);
  return null;
}

/**
 * **The warning, or `null` where the value goes out as written.** Names what goes out instead and
 * where the bound comes from, because an inherited floor is the case the operator cannot see.
 */
export function describeFloorBelowBound(where: string, authored: number, bound: FloorBound | null): string | null {
  if (!bound) return null;
  const source = bound.inheritedFrom ? `inherited from ${bound.inheritedFrom}` : 'set on this source';
  if (bound.fixed) {
    if (authored === bound.value) return null;
    return `\`${where}\` is ${authored}, but this source's CPM is fixed at ${bound.value} (${source}), so it goes out as ${bound.value}.`;
  }
  if (authored >= bound.value) return null;
  return `\`${where}\` is ${authored}, below this source's minimum floor of ${bound.value} (${source}), so it goes out as ${bound.value}.`;
}
